// @flow
import React, { useEffect, useState } from 'react';
import type { Node } from 'react';
import Debug from 'debug';
import OlVectorLayer from 'ol/layer/Vector';
import type { Source, Layer, Style } from '../types';
import { useMap } from '../context';
import { LayerProvider } from './context';

const debug = Debug('amms:modules:openlayers:layers:vector');

type Props = $ReadOnly<{
    label?: string,
    source: Source,
    style: Style,
    zIndex?: number,
    updateWhileAnimating?: boolean,
    updateWhileInteracting?: boolean,
    children: Node
}>;

function VectorLayer({ label = '', source, style, zIndex, updateWhileAnimating, updateWhileInteracting, children }: Props) {
    const [layer, setLayer] = useState<?Layer>();
    const map = useMap();

    useEffect(() => {
        let _layer;
        if(map) {
            debug(`Adding Vector layer ${label}`);

            _layer = new OlVectorLayer({
                label,
                source,
                zIndex,
                updateWhileAnimating,
                updateWhileInteracting
            });

            map.addLayer(_layer);
            setLayer(_layer);
        }

        return () => {
           if(map && _layer) {
               debug(`Removing Vector layer ${label}`);

               map.removeLayer(_layer);
               setLayer(null);
           }
        };
    }, [label, map, source, zIndex, updateWhileAnimating, updateWhileInteracting]);

    useEffect(() => {
        if(layer) {
            if(style) {
                debug(`Adding style ${label ? `to ${label}` : ''}: `, style);

                let _style = style;
                if(typeof style === 'function') {
                    const defaultStyleFunction = layer.getStyleFunction();
                    _style = (feature, resolution) => style(feature, resolution, defaultStyleFunction);
                }

                layer.setStyle(_style);
            }
        }
    }, [label, layer, style]);

    return (
        <LayerProvider layer={layer}>
            {children}
        </LayerProvider>
    );
}

export default VectorLayer;